/**
 * List View Component for Calendar
 */

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CalendarEvent } from '../../lib/api';
import { EventsByMonth } from './types';
import { EVENT_TYPE_COLORS, EVENT_TYPE_LABELS, ITEMS_PER_PAGE } from './constants';
import { getStatusBadgeClass } from './utils';
import { useFormatters } from '../../contexts/PreferencesContext';

interface ListViewProps {
  allListEvents: CalendarEvent[];
  paginatedEventsByMonth: EventsByMonth;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onEventClick: (event: CalendarEvent) => void;
}

export function ListView({
  allListEvents,
  paginatedEventsByMonth,
  currentPage,
  totalPages,
  onPageChange,
  onEventClick,
}: ListViewProps) {
  if (allListEvents.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No events found matching the selected filters
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Pagination Controls - Top */}
      {totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={allListEvents.length}
          onPageChange={onPageChange}
          position="top"
        />
      )}

      <div className="space-y-6">
        {Object.entries(paginatedEventsByMonth)
          .sort(([a], [b]) => b.localeCompare(a))
          .map(([monthKey, monthEvents]) => (
            <div key={monthKey} className="space-y-3">
              {/* Month Header */}
              <div className="sticky top-0 z-10 bg-background pb-2">
                <h3 className="text-lg font-semibold text-foreground border-b-2 border-primary pb-1">
                  {monthKey}
                </h3>
              </div>

              {/* Events for this month */}
              <div className="space-y-2">
                {monthEvents.map((event) => (
                  <EventListItem
                    key={event.id}
                    event={event}
                    onClick={() => onEventClick(event)}
                  />
                ))}
              </div>
            </div>
          ))}
      </div>

      {/* Pagination Controls - Bottom */}
      {totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={allListEvents.length}
          onPageChange={onPageChange}
          position="bottom"
        />
      )}
    </div>
  );
}

interface EventListItemProps {
  event: CalendarEvent;
  onClick: () => void;
}

function EventListItem({ event, onClick }: EventListItemProps) {
  const { formatDateTime } = useFormatters();
  const isExpired = new Date(event.end_datetime) < new Date();

  return (
    <div
      onClick={onClick}
      className={`p-4 rounded-lg border cursor-pointer hover:shadow-md transition-all ${
        isExpired ? 'bg-gray-50 opacity-70' : 'bg-white hover:bg-accent/5'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-3 h-3 rounded-full ${EVENT_TYPE_COLORS[event.event_type]}`} />
            <h4 className="font-semibold text-foreground">{event.title}</h4>
            {isExpired && (
              <Badge variant="outline" className="text-xs">
                Past Event
              </Badge>
            )}
          </div>
          {event.description && (
            <p className="text-sm text-muted-foreground mb-2">{event.description}</p>
          )}
          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span>
              <strong>Start:</strong> {formatDateTime(event.start_datetime)}
            </span>
            <span>
              <strong>End:</strong> {formatDateTime(event.end_datetime)}
            </span>
            {event.location && (
              <span>
                <strong>Location:</strong> {event.location}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Badge variant="outline">{EVENT_TYPE_LABELS[event.event_type]}</Badge>
          <Badge className={getStatusBadgeClass(event.status)}>
            {event.status.replace('_', ' ').toUpperCase()}
          </Badge>
        </div>
      </div>
    </div>
  );
}

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  position: 'top' | 'bottom';
}

function PaginationControls({
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  position,
}: PaginationControlsProps) {
  const borderClass = position === 'top' ? 'border-b pb-4 mb-4' : 'border-t pt-4';

  return (
    <div className={`flex items-center justify-between ${borderClass}`}>
      <div className="text-sm text-muted-foreground">
        Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{' '}
        {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems} events
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </Button>
        <div className="flex gap-1">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
            if (
              pageNum === 1 ||
              pageNum === totalPages ||
              (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
            ) {
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onPageChange(pageNum)}
                  className="min-w-[40px]"
                >
                  {pageNum}
                </Button>
              );
            } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
              return (
                <span key={pageNum} className="px-2">
                  ...
                </span>
              );
            }
            return null;
          })}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
